/**
 * Authentication Context for Microsoft Entra External ID
 *
 * Provides authentication state and methods throughout the application
 */

import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../config/authConfig';

/**
 * Authentication state interface
 */
export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  userProfileLoaded: boolean;
  user: {
    email: string;
    name: string;
    userId: string;
    tenantId: string;
    identityProvider?: string;
  } | null;
  error: string | null;
}

/**
 * Authentication context methods interface
 */
export interface AuthContextType extends AuthState {
  login: () => Promise<void>;
  logout: () => Promise<void>;
}

/**
 * Authentication Context
 */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Authentication Provider Props
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 *
 * Manages authentication state using MSAL and provides context to the app.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const [isLoading, setIsLoading] = useState(true);
  const [userProfileLoaded, setUserProfileLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if user is authenticated
  const isAuthenticated = accounts.length > 0;

  // Extract user information from MSAL account
  const user = isAuthenticated && accounts[0]
    ? {
        email: String(accounts[0].username || accounts[0].idTokenClaims?.email || ''),
        name: String(accounts[0].name || accounts[0].idTokenClaims?.name || ''),
        userId: String(accounts[0].localAccountId || accounts[0].homeAccountId || ''),
        tenantId: String(accounts[0].tenantId || ''),
        identityProvider: accounts[0].idTokenClaims?.idp as string | undefined,
      }
    : null;

  /**
   * Initialize authentication state and fetch user profile
   */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        setUserProfileLoaded(false);

        // Check if there are any accounts
        if (accounts.length > 0) {
          // Set the first account as active
          instance.setActiveAccount(accounts[0]);

          // Wait for token to be available before making API calls
          // This prevents 401 errors due to race conditions during initial login
          try {
            const { waitForToken } = await import('../services/authService');
            const token = await waitForToken(5000);

            if (!token) {
              console.warn('Token not available after waiting, skipping profile fetch');
              return;
            }

            // Token is ready - fetch user profile to ensure user is registered (JIT provisioning)
            const { httpWithAuth } = await import('../services/apiClient');
            const profile = await httpWithAuth('/api/user/profile');
            console.log('User profile fetched:', profile);
            // Profile information is now guaranteed to exist in backend database

            // Mark profile as loaded - other components can now safely call APIs
            setUserProfileLoaded(true);
          } catch (profileError) {
            console.error('Failed to fetch user profile:', profileError);
            // Profile fetch failure is logged but authentication state is maintained
            // User can still use the app, but database sync may be incomplete
          }
        } else {
          // No accounts - reset profile loaded flag
          setUserProfileLoaded(false);
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
        setError(err instanceof Error ? err.message : 'Authentication initialization failed');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [accounts, instance]);

  /**
   * Login method - always uses Popup flow
   */
  const login = async () => {
    try {
      setError(null);
      setIsLoading(true);

      console.log('Using loginPopup for authentication');
      const response = await instance.loginPopup(loginRequest);
      if (response.account) {
        instance.setActiveAccount(response.account);
      }
      setIsLoading(false);
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'Login failed');
      setIsLoading(false);
    }
  };

  /**
   * Logout method - always uses Popup flow
   */
  const logout = async () => {
    try {
      setError(null);
      setIsLoading(true);

      // Clear legacy token if exists
      localStorage.removeItem('advisio_access_token');
      localStorage.removeItem('advisio_user_email');
      localStorage.removeItem('advisio_auth_state');

      console.log('Using logoutPopup for sign out');
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
      });
      setIsLoading(false);
    } catch (err) {
      console.error('Logout error:', err);
      setError(err instanceof Error ? err.message : 'Logout failed');
      setIsLoading(false);
    }
  };

  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    userProfileLoaded,
    user,
    error,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
